import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, MapPin, Zap, Snail, Loader2, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMin = (m) => {
  if (m === null || m === undefined) return '—';
  if (m < 1) return '<1m';
  if (m >= 60) return `${(m / 60).toFixed(1)}h`;
  return `${Math.round(m)}m`;
};

// Choose a cell background that conveys "fast → slow" intensity. Green = fast,
// amber = ok, red = slow. Floor/ceiling tied to the overall avg so it scales
// per-company without a hard-coded threshold.
const cellColor = (minutes, p50, p90) => {
  if (minutes === null || minutes === undefined) return { bg: '#F8FAFC', fg: '#94A3B8' };
  if (minutes <= p50) return { bg: '#DCFCE7', fg: '#166534' };          // green
  if (minutes <= p90) return { bg: '#FEF3C7', fg: '#92400E' };          // amber
  return { bg: '#FEE2E2', fg: '#991B1B' };                              // red
};

const percentile = (arr, p) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const idx = Math.min(s.length - 1, Math.floor((p / 100) * s.length));
  return s[idx];
};

const DriverPerformanceHeatmap = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    setErr('');
    axios
      .get(`${API}/analytics/driver-performance?days=${days}`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load driver performance'))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6 flex items-center gap-2 text-sm text-slate-500" data-testid="dwell-heatmap-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading driver performance…
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800" data-testid="dwell-heatmap-error" role="alert">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const { total_stops_measured, overall_avg_min, per_driver, per_location, matrix, insights } = data;

  // Pre-compute the percentiles once over all matrix cells with values.
  const cellValues = matrix.rows.flatMap((r) => r.cells.map((c) => c.avg_min).filter((v) => v !== null));
  const p50 = percentile(cellValues, 50);
  const p90 = percentile(cellValues, 90);

  return (
    <div data-testid="dwell-heatmap-section" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <Clock className="w-5 h-5 text-[#002FA7]" />
            Driver Dwell Performance
          </h2>
          <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Average <strong>arrived → departed</strong> time per stop. Lower is better.
            {total_stops_measured > 0 && <> Based on {total_stops_measured} measured stops over the last {days} days.</>}
          </p>
        </div>
        <div className="relative">
          <select
            data-testid="dwell-window-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="appearance-none bg-white border border-slate-300 rounded-md px-3 py-1.5 pr-7 text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#002FA7] focus:outline-none cursor-pointer"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
          </select>
          <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {total_stops_measured === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center" data-testid="dwell-heatmap-empty">
          <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            No completed stops yet in this window. Once drivers check in at pickups & deliveries, performance data will appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Insights row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InsightCard
              testid="insight-overall-avg"
              icon={Clock}
              tone="neutral"
              label="OVERALL AVG"
              primary={fmtMin(overall_avg_min)}
              detail={`across ${total_stops_measured} stops`}
            />
            <InsightCard
              testid="insight-rockstars"
              icon={Zap}
              tone="success"
              label="ROCKSTARS"
              primary={insights.rockstars[0]?.driver_name || '—'}
              detail={insights.rockstars[0] ? `${fmtMin(insights.rockstars[0].avg_min)} avg · ${insights.rockstars[0].stops_count} stops` : 'need 3+ stops per driver'}
            />
            <InsightCard
              testid="insight-slow-locations"
              icon={Snail}
              tone="danger"
              label="SLOWEST SHIPPER/RECEIVER"
              primary={insights.slow_locations[0]?.location || '—'}
              detail={insights.slow_locations[0] ? `${fmtMin(insights.slow_locations[0].avg_min)} avg · ${insights.slow_locations[0].stops_count} stops` : 'no recurring slow spots yet'}
            />
          </div>

          {/* Heat-map matrix: Drivers × Brokers */}
          {matrix.brokers.length > 0 && matrix.rows.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="dwell-matrix">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Drivers × Brokers — Avg Dwell
                </h3>
                <Legend p50={p50} p90={p90} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10">Driver</th>
                      {matrix.brokers.map((b) => (
                        <th key={b} className="text-center py-2 px-2 font-semibold text-slate-600 whitespace-nowrap" title={b}>
                          {b.length > 14 ? `${b.slice(0, 12)}…` : b}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.rows.map((row) => (
                      <tr key={row.driver_id || row.driver_name} className="border-t border-slate-100">
                        <td className="py-2 px-3 font-medium text-slate-900 sticky left-0 bg-white z-10 whitespace-nowrap" data-testid={`dwell-row-${row.driver_id || row.driver_name}`}>
                          {row.driver_name}
                        </td>
                        {row.cells.map((c) => {
                          const color = cellColor(c.avg_min, p50, p90);
                          return (
                            <td
                              key={c.broker}
                              className="py-2 px-2 text-center font-bold"
                              style={{ backgroundColor: color.bg, color: color.fg, minWidth: 70 }}
                              title={c.count > 0 ? `${fmtMin(c.avg_min)} avg over ${c.count} stops` : 'No data'}
                              data-testid={`dwell-cell-${row.driver_id || row.driver_name}-${c.broker}`}
                            >
                              {c.avg_min === null ? '—' : fmtMin(c.avg_min)}
                              {c.count > 0 && (
                                <span className="block text-[9px] font-normal opacity-60 mt-0.5">{c.count}{c.count === 1 ? ' stop' : ' stops'}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per-driver detail table */}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="dwell-per-driver">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>By Driver</h3>
            </div>
            <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Driver</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Pickups Avg</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Deliveries Avg</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Stops</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Range</th>
                </tr>
              </thead>
              <tbody>
                {per_driver.map((d) => (
                  <tr key={d.driver_id || d.driver_name} className="border-t border-slate-100" data-testid={`dwell-driver-${d.driver_id || d.driver_name}`}>
                    <td className="py-2 px-3 font-medium text-slate-900">{d.driver_name}</td>
                    <td className="py-2 px-3 text-right text-slate-700">{fmtMin(d.pickups_avg_min)}</td>
                    <td className="py-2 px-3 text-right text-slate-700">{fmtMin(d.deliveries_avg_min)}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{d.stops_count}</td>
                    <td className="py-2 px-3 text-right text-slate-500 text-xs">{fmtMin(d.min_min)} – {fmtMin(d.max_min)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Slow spots */}
          {per_location.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="dwell-per-location">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#002FA7]" />
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Location Hotspots</h3>
              </div>
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Location</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Type</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Avg Dwell</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Stops</th>
                  </tr>
                </thead>
                <tbody>
                  {per_location.slice(0, 12).map((l, idx) => {
                    const color = cellColor(l.avg_min, p50, p90);
                    return (
                      <tr key={`${l.location}-${idx}`} className="border-t border-slate-100">
                        <td className="py-2 px-3 font-medium text-slate-900">{l.location}</td>
                        <td className="py-2 px-3 text-slate-500 capitalize text-xs">{l.kind}</td>
                        <td className="py-2 px-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded font-bold text-xs" style={{ backgroundColor: color.bg, color: color.fg }}>
                            {fmtMin(l.avg_min)}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right text-slate-600">{l.stops_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const InsightCard = ({ icon: Icon, label, primary, detail, tone, testid }) => {
  const tones = {
    success: { bg: 'bg-green-50', border: 'border-green-200', accent: 'text-green-700' },
    danger:  { bg: 'bg-red-50',   border: 'border-red-200',   accent: 'text-red-700' },
    neutral: { bg: 'bg-white',    border: 'border-slate-200', accent: 'text-[#002FA7]' },
  }[tone] || { bg: 'bg-white', border: 'border-slate-200', accent: 'text-slate-700' };
  return (
    <div data-testid={testid} className={`${tones.bg} ${tones.border} border rounded-md p-4`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${tones.accent}`} strokeWidth={2.25} />
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
      </div>
      <p className="text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{primary}</p>
      <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{detail}</p>
    </div>
  );
};

const Legend = ({ p50, p90 }) => (
  <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
    <span className="flex items-center gap-1">
      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#DCFCE7' }} />
      <span className="text-green-700">Fast ≤ {fmtMin(p50)}</span>
    </span>
    <span className="flex items-center gap-1">
      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#FEF3C7' }} />
      <span className="text-amber-700">OK ≤ {fmtMin(p90)}</span>
    </span>
    <span className="flex items-center gap-1">
      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#FEE2E2' }} />
      <span className="text-red-700">Slow</span>
    </span>
  </div>
);

export default DriverPerformanceHeatmap;
